import type { StepOptions, StepOptionsButton } from 'shepherd.js'
import type { TourDefinition } from '../registry'
import { back, finish, gotIt, next } from '../utils/buttons'
import { popoverOffset } from '../utils/popover-offset'
import { waitForSelector } from '../utils/wait-for-selector'

/**
 * Lazy-load the router so the tour registry doesn't drag in the full
 * routeTree (which transitively pulls pdfjs-dist via the order PDF viewer
 * and breaks the jsdom-based unit tests with "DOMMatrix is not defined").
 */
async function navigateTo(to: string): Promise<void> {
  const { router } = await import('../../../router')
  await router.navigate({ to })
}

/**
 * Post-signup orientation that walks a fresh demo-day user through the
 * workspace we just generated for them: the dashboard tiles, the auto-
 * populated organization pages (profile / value prop / ICP), and the
 * sidebar inbox where their daily insights will land.
 *
 * Trigger model: `manual`. The dashboard launches the tour explicitly via
 * `startTour('demoday-orientation')` when it consumes the demoday handoff
 * flag — so the feature-flag gate that `useAutoTour` enforces does not
 * apply, and the tour fires reliably right after signup.
 *
 * Multi-page strategy: each "Next" button on a step that lives on a
 * different page also calls `router.navigate({to: …})`. Shepherd's
 * per-step `beforeShowPromise` (set in default-step-options) waits up to
 * 1.5 s for the next step's anchor to mount after navigation, so popovers
 * appear once the destination page has rendered. Anchors are `data-tour`
 * attributes on stable elements (the org-page Card wrappers, the dashboard
 * Quick Action links, the dashboard greeting), not on data-loaded fields,
 * so we don't race the server.
 */

/** Build a Next-style button that also navigates the router on click. */
function nextAndGo(to: string): StepOptionsButton {
  return {
    text: 'Next',
    action() {
      void navigateTo(to)
      return this.next()
    },
  }
}

/** Build a Back-style button that navigates the router on click. */
function backAndGo(to: string): StepOptionsButton {
  return {
    text: 'Back',
    classes: 'shepherd-button-secondary',
    action() {
      void navigateTo(to)
      return this.back()
    },
  }
}

/**
 * Org-page steps need three things that the tour-wide defaults don't give
 * them on their own:
 *
 *   1. The `/organization/*` parent route has `pendingComponent:
 *      SettingsPageSkeleton` plus an async loader fetching
 *      `getCompanyInfoAppOrganizationCompanyInfoGet`, so the child's Card
 *      (where `data-tour="org-*-card"` lives) does not mount until that
 *      fetch resolves. The default `beforeShowPromise` waits only 1.5 s —
 *      we bump it to 8 s for these steps.
 *
 *   2. Shepherd's `Tour.show()` checks `showOn` *synchronously* before it
 *      ever invokes `beforeShowPromise`. The button's `router.navigate({to})`
 *      hasn't completed by the time `this.next()` triggers the check, so
 *      `document.querySelector(...)` returns null at that instant — the
 *      default `showOn` then returns false and the step is silently
 *      skipped, never giving the longer wait a chance to run. Override
 *      `showOn` to unconditionally return true; we'll let
 *      `beforeShowPromise` do the actual waiting.
 *
 *   3. If the anchor genuinely never appears (route failure, query error),
 *      Shepherd falls back to a centered modal-style popover — which is
 *      acceptable for this tour. Better than silently swallowing the
 *      whole org-page section of the orientation.
 */
const ORG_PAGE_LOADER_TIMEOUT_MS = 8000

function waitForAnchor(this: StepOptions): Promise<void> {
  const sel = this.attachTo?.element
  if (typeof sel !== 'string') {
    return Promise.resolve()
  }
  return waitForSelector(sel, { timeoutMs: ORG_PAGE_LOADER_TIMEOUT_MS })
}

function alwaysShow(): boolean {
  return true
}

export const demodayOrientationTour: TourDefinition = {
  id: 'demoday-orientation',
  displayName: 'Demo-day orientation',
  featureFlag: 'tour-demoday-orientation',
  trigger: 'manual',
  targetPath: '/',
  steps: [
    // ---------- Step 1: Welcome on the dashboard ----------
    {
      id: 'welcome',
      title: 'Welcome in',
      text:
        'This is your workspace. We set it up from your website while you signed up. Want a quick tour of what\'s here?',
      attachTo: { element: '[data-tour="dashboard-greeting"]', on: 'bottom' },
      floatingUIOptions: popoverOffset(0, 16),
      buttons: [gotIt],
    },

    // ---------- Steps 2-3: Dashboard tiles (still on /) ----------
    {
      id: 'view-accounts',
      title: 'Start here: a ranked prospect list',
      text:
        'A list of accounts, ranked by how good a prospect each one might be for your team to sell to. We\'ll show you what shapes that ranking in a few steps.',
      attachTo: { element: '[data-tour="home-view-accounts"]', on: 'bottom' },
      floatingUIOptions: popoverOffset(0, 12),
      buttons: [back, next],
    },
    {
      id: 'market-research',
      title: 'Market research reports',
      text:
        'AI-written reports on industry segments — market size, growth drivers, key players, and where your offer fits. Useful when you\'re sizing up a new vertical or briefing the team before a campaign.',
      attachTo: { element: '[data-tour="home-market-research"]', on: 'bottom' },
      floatingUIOptions: popoverOffset(0, 12),
      // Hand off to the company-profile page on Next.
      buttons: [back, nextAndGo('/organization/organization-profile')],
    },

    // ---------- Step 4: Org profile page ----------
    {
      id: 'profile',
      title: 'Your company profile',
      text:
        'This is one of the things we use to score prospects. Pulled from your website — edit anything that\'s off.',
      attachTo: { element: '[data-tour="org-profile-card"]', on: 'right' },
      floatingUIOptions: popoverOffset(16, 0),
      beforeShowPromise: waitForAnchor,
      showOn: alwaysShow,
      // Back goes home, Next moves to the offering page.
      buttons: [backAndGo('/'), nextAndGo('/organization/offering')],
    },

    // ---------- Step 5: Offering / value proposition ----------
    {
      id: 'offering',
      title: 'What you sell',
      text:
        'Another piece of the scoring context — how you describe your offer and who it\'s for. We took a first pass; tighten it in your own voice when you have a chance.',
      attachTo: { element: '[data-tour="org-offering-card"]', on: 'right' },
      floatingUIOptions: popoverOffset(16, 0),
      beforeShowPromise: waitForAnchor,
      showOn: alwaysShow,
      buttons: [
        backAndGo('/organization/organization-profile'),
        nextAndGo('/organization/customers'),
      ],
    },

    // ---------- Step 6: ICP scoring rubric (the reveal) ----------
    {
      id: 'customers',
      title: 'Your Ideal Customer Profile — the scoring rubric',
      text:
        'These criteria are the rubric we score every account against. Combined with your profile and offering, this is why one account scores higher than another. Adjust the criteria here and every score updates.',
      attachTo: { element: '[data-tour="org-customers-card"]', on: 'right' },
      floatingUIOptions: popoverOffset(16, 0),
      beforeShowPromise: waitForAnchor,
      showOn: alwaysShow,
      // Back hops to offering, Next sends us home for the wrap-up.
      buttons: [backAndGo('/organization/offering'), nextAndGo('/')],
    },

    // ---------- Step 7: Closing CTA on the dashboard ----------
    {
      id: 'closing',
      title: 'Working the list',
      text:
        'In Accounts you can filter the list, assign accounts to reps or distributors, leave notes, pull contacts, and reach out. That\'s where most of the daily work happens.',
      attachTo: { element: '[data-tour="home-view-accounts"]', on: 'bottom' },
      floatingUIOptions: popoverOffset(0, 12),
      buttons: [backAndGo('/organization/customers'), next],
    },

    // ---------- Step 8: Sidebar Inbox ----------
    {
      id: 'inbox',
      title: 'And new signals land here',
      text:
        'When Scout surfaces a new action — research a contact, follow up on a buying signal — it shows up in your Inbox. That\'s your daily queue.',
      attachTo: { element: '[data-tour="sidebar-inbox"]', on: 'right' },
      floatingUIOptions: popoverOffset(16, 0),
      buttons: [back, finish],
    },
  ],
}
